import { useState, useEffect } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';

import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Rating,
  Chip,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Slider,
  Pagination,
  Skeleton,
  LinearProgress,
  Avatar,
  Card,
  CardContent,
  IconButton,
  FormControlLabel,
  Checkbox,
  Collapse,
  Tooltip, 
  Button,
  
  
  
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Star as StarIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingIcon,
  AttachMoney as MoneyIcon,
  Info as InfoIcon,
  People as PeopleIcon, 
  Language as LanguageIcon,
  Warning as WarningIcon,
  AddBusinessOutlined as AddBusinessIcon,
  SearchOutlined as SearchIcon,
  Map as MapIcon

} from '@mui/icons-material';

import BusinessMap from './BusinessMap';

// 🆕 SEO imports
import { Helmet } from 'react-helmet-async';
import EnhancedSEO from '../../Components/SEO/EnhancedSEO';


import { useSelector } from 'react-redux'; // For auth state
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSpFindBusinessNameMutation } from '../../redux/thunks/unauth/spfindbusinessname';

import { usePrefetch as useGetUserCacheDataPrefetch } from '../../redux/thunks/users/usercachedata';
import { useLazyGetUserCacheDataQuery } from '../../redux/thunks/users/usercachedata';
import { useLazyGetBusinessesByQuickQryQuery } from '../../redux/thunks/unauth/getbusinessesbyquickqry';

import NoResultsFound from './NoResultsFound';  // Create this component in a separate file

import MobileFilterDrawer from './SearchResultsPage/MobileFilterDrawer';
import MobileActionBar from './SearchResultsPage/MobileActionBar';
import QuickFilterChips from './SearchResultsPage/QuickFilterChips';
import CollapsibleInsights from './SearchResultsPage/CollapsibleInsights';
import CollapsibleFilters from './SearchResultsPage/CollapsibleFilters';






const calculateDistance = (userLat, userLng, businessLat, businessLng) => {
  if (!businessLat || !businessLng) return null;
  
  // Haversine formula implementation
  const R = 3959; // Earth's radius in miles
  const dLat = (businessLat - userLat) * Math.PI / 180;
  const dLng = (businessLng - userLng) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(userLat * Math.PI / 180) * Math.cos(businessLat * Math.PI / 180) * 
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// Helper function to calculate distance between two coordinate pairs
const calculateDistanceBetweenCoordinates = (coords1, coords2) => {
  if (!Array.isArray(coords1) || !Array.isArray(coords2) || 
      coords1.length < 2 || coords2.length < 2) {
    return Infinity; // Return Infinity for invalid coordinates to ensure they don't match distance filters
  }
  
  // coords1 and coords2 are both in [longitude, latitude] format
  const long1 = coords1[0];
  const lat1 = coords1[1];
  const long2 = coords2[0];
  const lat2 = coords2[1];
  
  return calculateDistance(lat1, long1, lat2, long2);
};



const CATEGORY_TYPES = {
  PHYSICAL: 'physical',
  ONLINE: 'online'
};


//We need a logic to render categories on the fly based on the search results, Show top 5 categories


const SORT_OPTIONS = [
  { value: 'relevance', label: 'Most Relevant' },
  { value: 'rating', label: 'Highest Rated' },
  { value: 'reviews', label: 'Most Reviewed' },
  { value: 'distance', label: 'Nearest' }
];


// Distance filter options
const DISTANCE_OPTIONS = [
  { label: 'Any Distance', value: 'all' },
  { label: 'Within 5 miles', value: 5 },
  { label: 'Within 10 miles', value: 10 },
  { label: 'Within 25 miles', value: 25 },
  { label: 'Within 50 miles', value: 50 }
];

// Mock Insights Data
const INSIGHTS = [
  {
    title: "Businesses Added Recently",
    content: "We’ve added 50 new businesses in the past week! Explore now to find your next favorite place.",
    icon: <TrendingIcon color="primary" />,
  },
  {
    title: "Popular Categories",
    content: "Top categories include Restaurants, Cafes, and Fitness Centers. Discover businesses in your favorite category.",
    icon: <StarIcon color="primary" />,
  },
  {
    title: "Claim Your Business",
    content: "80% of businesses are unclaimed. Claim yours today to start receiving reviews and insights!",
    icon: <AddBusinessIcon color="primary" />,
  },
  {
    title: "Nearby Businesses",
    content: "Discover businesses within 5 miles of your location. Start exploring local favorites now!",
    icon: <LocationIcon color="primary" />,
  },
  {
    title: "Top Features",
    content: "Most businesses offer Free WiFi, Parking, and Pet-Friendly spaces. See what’s available near you.",
    icon: <InfoIcon color="primary" />,
  },
];

// Skeleton Components
const ResultCardSkeleton = () => (
  <Paper variant='outlined' sx={{ p: 2, mb: 2, border: 'none' }}>
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Skeleton variant="circular" width={64} height={64} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width="60%" height={28} />
        <Skeleton variant="text" width="40%" height={24} />
        <Skeleton variant="text" width="30%" height={24} />
      </Box>
    </Box>
    <Skeleton variant="text" width="100%" sx={{ mt: 2 }} />
    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
      <Skeleton variant="rounded" width={80} height={32} />
      <Skeleton variant="rounded" width={80} height={32} />
      <Skeleton variant="rounded" width={80} height={32} />
    </Box>
  </Paper>
);

const InsightsSkeleton = () => (
  <Paper variant='outlined' sx={{ p: 2, border: 'none' }}>
    <Skeleton variant="text" width="60%" height={32} />
    <Skeleton variant="rectangular" height={100} sx={{ mt: 2 }} />
    <Skeleton variant="text" width="40%" height={24} sx={{ mt: 2 }} />
    <Skeleton variant="rectangular" height={80} sx={{ mt: 1 }} />
    <Skeleton variant="text" width="50%" height={24} sx={{ mt: 2 }} />
    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
      <Skeleton variant="rounded" width={100} height={32} />
      <Skeleton variant="rounded" width={100} height={32} />
    </Box>
  </Paper>
);

const getRandomInsights = (count = 2) => {
  const shuffled = [...INSIGHTS].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
};


const computeCategoryOptions = (searchResults) => {
  // First, count occurrences of each skillName
  const categoryCount = searchResults.reduce((acc, result) => {
    const skillName = result.skillName;
    acc[skillName] = (acc[skillName] || 0) + 1;
    return acc;
  }, {});

  // Convert to array and sort by count
  const sortedCategories = Object.entries(categoryCount)
    .sort((a, b) => b[1] - a[1]); // Sort by count in descending order

  // Get top 5 categories
  const topCategories = sortedCategories.slice(0, 5).map(([skillName]) => ({
    label: skillName,
    value: skillName.toLowerCase(),
    type: CATEGORY_TYPES.PHYSICAL, // You might want to determine this dynamically
    count: categoryCount[skillName]
  }));

  // Calculate "Others" count - sum of all categories not in top 5
  const othersCount = sortedCategories.slice(5).reduce((sum, [_, count]) => sum + count, 0);

  // Construct final category options
  const categoryOptions = [
    { label: 'All Categories', value: 'all', type: null, count: searchResults.length },
    ...topCategories
  ];

  // Add "Others" category if there are remaining categories
  if (othersCount > 0) {
    categoryOptions.push({
      label: 'Others',
      value: 'others',
      type: null,
      count: othersCount
    });
  }

  return categoryOptions;
};

// 🆕 SEO Helper Functions
const generateSearchResultsSchema = (query, location, results, totalResults) => {
  return {
    "@context": "https://schema.org",
    "@type": "SearchResultsPage",
    "mainEntity": {
      "@type": "ItemList",
      "name": `Search Results for "${query}"${location ? ` in ${location}` : ''}`,
      "description": `Find local businesses for ${query}${location ? ` in ${location}` : ''}`,
      "numberOfItems": totalResults,
      "itemListElement": results.slice(0, 10).map((business, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "LocalBusiness",
          "name": business.businessName,
          "description": business.description,
          "url": `https://www.revfer.com/business/${business._id}/${business.businessName.replace(/[\s,\.]+/g, '-').toLowerCase()}`,
          "category": business.skillName,
          ...(business.avgSkillRating_all && {
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": business.avgSkillRating_all,
              "reviewCount": business.count_all,
              "bestRating": 5,
              "worstRating": 1
            }
          }),
          ...(business.geocodeResults?.formattedAddress && {
            "address": {
              "@type": "PostalAddress",
              "streetAddress": business.geocodeResults.formattedAddress
            }
          }),
          ...(business.geocodeResults?.location?.coordinates && {
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": business.geocodeResults.location.coordinates[1],
              "longitude": business.geocodeResults.location.coordinates[0]
            }
          })
        }
      }))
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://www.revfer.com/searchresults?query={search_term_string}&location={location}"
      },
      "query-input": "required name=search_term_string"
    }
  };
};

const generateLocalBusinessSchema = (businesses) => {
  return businesses.slice(0, 5).map(business => ({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": business.businessName,
    "description": business.description,
    "url": `https://www.revfer.com/business/${business._id}/${business.businessName.replace(/[\s,\.]+/g, '-').toLowerCase()}`,
    "category": business.skillName,
    ...(business.avgSkillRating_all && {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": business.avgSkillRating_all,
        "reviewCount": business.count_all,
        "bestRating": 5,
        "worstRating": 1
      }
    }),
    ...(business.geocodeResults?.formattedAddress && {
      "address": {
        "@type": "PostalAddress",
        "streetAddress": business.geocodeResults.formattedAddress
      }
    }),
    ...(business.geocodeResults?.location?.coordinates && {
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": business.geocodeResults.location.coordinates[1],
        "longitude": business.geocodeResults.location.coordinates[0]
      }
    })
  }));
};



// Main Component
export default function SearchResultsPage() {
  const locationData = useLocation();
  const navigate = useNavigate();
  const randomInsights = getRandomInsights();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));


  const { query, userFlag, location: searchLocation, isNaturalLanguageSearch } = locationData.state || {};

  //const searchResultsTmp = locationData.state?.response || '';
  //const cacheId = searchResultsTmp?.data?.cacheId;
  //const searchResultsData = searchResultsTmp?.data?.results || [];
  //const searchParams = locationData.state?.searchParams || [];
  const [loading, setLoading] = useState(true);
  //const [searchResults, setSearchResults] = useState([]);
  const [category, setCategory] = useState('All Categories');
  const [ratingFilter, setRatingFilter] = useState(0);
  const [sortBy, setSortBy] = useState('relevance');
  const [page, setPage] = useState(1);
  const [resultsPerPage] = useState(5);
  const [location, setLocation] = useState('all');
  const [latestResults, setLatestResults] = useState([]);

  const [filteredResults, setFilteredResults] = useState([]);

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [maxDistance, setMaxDistance] = useState('all');
  const [userCoordinates, setUserCoordinates] = useState({
    lat: null,
    lng: null,
    loading: true,
    error: null
  });

  const [cacheId, setCacheId] = useState('');
  const [fetchResultsTrigger, setFetchResultsTrigger] = useState(false);
  const [showInsidersOnly, setShowInsidersOnly] = useState(false);

  const [showMap, setShowMap] = useState(false);
  const [searchCenterCoordinates, setSearchCenterCoordinates] = useState(null); // Added state for search center coordinates

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const [authReady, setAuthReady] = useState(false);




  /*States to show and hide certain components*/
  const [notHide, setNotHide] = useState(false);

  const token = useSelector((state) => state.auth?.token || null);  


  console.log('query:', query)
  console.log('userFlag', userFlag)


  
  //console.log('searchLocation', searchLocation)
  
  console.log('cacheId', cacheId)
  console.log('latestResults', latestResults)
  console.log('loading', loading)
  console.log('filteredResults1', filteredResults)
  
  const { authStatus } = useAuthenticator(context => [context.authStatus]);

  const isAuthenticated = authStatus === 'authenticated';


  // Determine if selected category is physical location based
  const selectedCategoryData = categoryOptions.find(cat => cat.value === category);
  const isPhysicalLocation = selectedCategoryData?.type === CATEGORY_TYPES.PHYSICAL;

  const [ triggerSearch, { data: MysearchResultsData, isLoading: isBusinessLoading }, ] = useSpFindBusinessNameMutation();
  const prefetchGetUserCacheData = useGetUserCacheDataPrefetch('getUserCacheData');
  const [getCacheData, { data: resultsCacheData, isLoading: cacheLoading }] = useLazyGetUserCacheDataQuery();

  const [getBusinessQuickQry, { data: quickBusinessesQryData, isLoading: isQuickBusinessQryLoading }] = useLazyGetBusinessesByQuickQryQuery();

  console.log('MysearchResultsData', MysearchResultsData)
  console.log('quickBusinessesQryData', quickBusinessesQryData)
  console.log('userCoordinates', userCoordinates)

  // 🆕 SEO Data Generation
  const generateSEOData = () => {
    const searchQuery = query || 'businesses';
    const searchLocationText = searchLocation || '';
    const totalResults = filteredResults.length;
    const hasResults = totalResults > 0;
    
    // Dynamic title based on search parameters
    const titleParts = [];
    if (searchQuery) titleParts.push(searchQuery.charAt(0).toUpperCase() + searchQuery.slice(1));
    if (searchLocationText) titleParts.push(`in ${searchLocationText}`);
    titleParts.push('- Business Search Results');
    titleParts.push('Revfer');
    
    const title = titleParts.join(' ');
    
    // Dynamic description
    const description = hasResults 
      ? `Found ${totalResults} ${searchQuery || 'businesses'}${searchLocationText ? ` in ${searchLocationText}` : ''}. Compare ratings, reviews, and find trusted local businesses through our AI-powered platform.`
      : `Search results for ${searchQuery || 'businesses'}${searchLocationText ? ` in ${searchLocationText}` : ''}. Discover local businesses with authentic reviews and AI-powered recommendations on Revfer.`;
    
    // Dynamic keywords
    const keywords = [
      searchQuery,
      searchLocationText,
      'business search results',
      'local business directory',
      'business reviews',
      'find businesses near me',
      'local business search',
      'business ratings',
      'trusted businesses',
      'AI business recommendations'
    ].filter(Boolean).join(', ');

    return { title, description, keywords, hasResults, totalResults };
  };

  useEffect(() => {
    if (authStatus === 'configuring') return;
    
    // If token exists in Redux but Amplify hasn't caught up, wait
    if (token && authStatus !== 'authenticated') return;
    
    // Auth is fully resolved
    setAuthReady(true);
  }, [authStatus, token]);



  

  useEffect(() => {
    if (!authReady) return;

    const currentlyAuthenticated = authStatus === 'authenticated';
    const currentToken = currentlyAuthenticated ? token : null;

    const auth = {
      token: currentToken,
      authType: currentlyAuthenticated ? 'token' : 'none',
    };

    if (query && !userFlag) {
      const payload = {
        auth,
        text: query,
        searchLocation,
        isNaturalLanguageSearch: isNaturalLanguageSearch || false
      };
      console.log('Triggering search with payload:', payload);
      triggerSearch({ payload });
    }

    if (query && userFlag) {
      if (currentlyAuthenticated) {
        const payload = {
          token: currentToken,
          query,
        };
        getBusinessQuickQry(payload);
      }
    }
  }, [authReady, query, triggerSearch, userFlag, getBusinessQuickQry, searchLocation, isNaturalLanguageSearch, authStatus, token]);

  useEffect(() => {
    if (MysearchResultsData) {
      // Extract coordinates
      const searchCoordinates = MysearchResultsData.coordinates;
      setSearchCenterCoordinates(searchCoordinates);
      
      // Process results and add distance information
      const resultsWithDistance = MysearchResultsData.results.map(result => {
        // Create a new object with all properties
        const newResult = { ...result };
        
        // Add distance if coordinates are available
        if (searchCoordinates && 
            Array.isArray(searchCoordinates) && 
            result.geocodeResults?.location?.coordinates) {
          
          const distance = calculateDistance(
            searchCoordinates[1], // lat from search center
            searchCoordinates[0], // lng from search center
            result.geocodeResults.location.coordinates[1], // lat from business
            result.geocodeResults.location.coordinates[0]  // lng from business
          );
          
          newResult.distanceFromSearchCenter = distance;
        }
        
        return newResult;
      });
      
      setLatestResults(resultsWithDistance);
      setCacheId(MysearchResultsData.cacheId);
      setFilteredResults(resultsWithDistance);
    }
  }, [MysearchResultsData]);

  useEffect(() => {
    if (quickBusinessesQryData) {
      // Extract coordinates
      const searchCoordinates = quickBusinessesQryData.coordinates;
      setSearchCenterCoordinates(searchCoordinates);
      
      // Process results and add distance information
      const resultsWithDistance = quickBusinessesQryData.results.map(result => {
        // Create a new object with all properties
        const newResult = { ...result };
        
        // Add distance if coordinates are available
        if (searchCoordinates && 
            Array.isArray(searchCoordinates) && 
            result.geocodeResults?.location?.coordinates) {
          
          const distance = calculateDistance(
            searchCoordinates[1], // lat from search center
            searchCoordinates[0], // lng from search center
            result.geocodeResults.location.coordinates[1], // lat from business
            result.geocodeResults.location.coordinates[0]  // lng from business
          );
          
          newResult.distanceFromSearchCenter = distance;
        }
        
        return newResult;
      });
      
      setLatestResults(resultsWithDistance);
      setCacheId(quickBusinessesQryData.cacheId);
      setFilteredResults(resultsWithDistance);
    }
  }, [quickBusinessesQryData]);

 

  useEffect(() => {
    setLoading(isBusinessLoading || cacheLoading || isQuickBusinessQryLoading)
    
  }, [isBusinessLoading, cacheLoading, isQuickBusinessQryLoading]);


   // Get user's location when component mounts
   useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserCoordinates({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            loading: false,
            error: null
          });
          // If we don't have search center coordinates yet, use user location as fallback
          if (!searchCenterCoordinates || (searchCenterCoordinates[0] === 0 && searchCenterCoordinates[1] === 0)) {
            setSearchCenterCoordinates([position.coords.longitude, position.coords.latitude]);
          }
        },
        (error) => {
          setUserCoordinates(prev => ({
            ...prev,
            loading: false,
            error: 'Unable to get your location'
          }));
        }
      );
    } else {
      setUserCoordinates(prev => ({
        ...prev,
        loading: false,
        error: 'Geolocation not supported'
      }));
    }
  }, []);

  useEffect(() => {
    if (latestResults.length > 0) {
      const options = computeCategoryOptions(latestResults);
      setCategoryOptions(options);
      
      // Reset category selection if current category is no longer valid
      if (!options.find(opt => opt.value === category)) {
        setCategory('All Categories');
      }
    }
  }, [latestResults]);



  useEffect(() => {
      if (resultsCacheData) {
        console.log('resultsCacheData', resultsCacheData)
        const uniqueObjectIdsArray = resultsCacheData.uniqueObjectIdsArray || [];
        const stringObjectIds = uniqueObjectIdsArray.map((objectId) => objectId.businessId.toString());
        console.log('uniqueObjectIdsArray', uniqueObjectIdsArray)
        console.log('stringObjectIds', stringObjectIds)
        console.log('latestResults', latestResults)

  
        const filteredLatestResults = latestResults.filter((business) =>
          stringObjectIds.includes(business._id)
        );
        console.log('filteredLatestResults', filteredLatestResults)

        showInsidersOnly && setLatestResults(filteredLatestResults);
        !showInsidersOnly && setLatestResults(MysearchResultsData?.results || quickBusinessesQryData?.results || []);
  
  
      }

      
  }, [resultsCacheData, fetchResultsTrigger, MysearchResultsData, quickBusinessesQryData, showInsidersOnly]);

  
  useEffect(() => {

    console.log('Filter here, ', latestResults)
    

   
      // Filter and sort results
      const filteredResultsData = latestResults
      .filter(result => {
        // Basic category filter
        if (category !== 'All Categories') {
          console.log('aaaaaa')
          if (category === 'others') {
            console.log('bbbbbbb')
            
            // For "others" category, exclude items that are in top 5
            const topCategories = categoryOptions
              .filter(opt => opt.value !== 'all' && opt.value !== 'others')
              .map(opt => opt.value);
            if (topCategories.includes(result.skillName.toLowerCase())) {
              return false;
            }
          } else {
            console.log('cccccc')
            // For specific category selection
            if (result.skillName.toLowerCase() !== category) {
              console.log('dddddd')
              return false;
            }
          }
        }
        if (maxDistance !== 'all' && searchCenterCoordinates && searchCenterCoordinates.length === 2) {
          console.log('eeeeeee')
          if (!result.geocodeResults?.location?.coordinates) {
            console.log('fffffff')
            // Keep businesses without coordinates but they'll show with a warning
            return true;
          }
                // Distance filter - using the pre-calculated distance
          if (maxDistance !== 'all' && result.distanceFromSearchCenter !== undefined) {
            if (result.distanceFromSearchCenter > maxDistance) {
              return false;
            }
          }

         
     
   
        }
      
        
        // Rating filter
        if (ratingFilter > 0 && result.avgSkillRating_all < ratingFilter) {
          console.log('hhhhhh')
          return false;
        
        }
        
        // Location filter - only apply if physical location
        if (isPhysicalLocation && 
            location !== 'all' && 
            result.physicalLocationDetails?.neighborhood.toLowerCase() !== location) {
              console.log('jjjjjj')
          return false;
        }

        console.log('check here')
        
        
        
        return true;
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'rating':
            return b.avgSkillRating_all - a.avgSkillRating_all;
          case 'reviews':
            return b.reviews - a.reviews;
          case 'distance':
            return (a.distanceFromSearchCenter || Infinity) - (b.distanceFromSearchCenter || Infinity);
          default:
            return 0;
        }
  });    
     console.log('filteredResultsData', filteredResultsData)
    
    setFilteredResults(filteredResultsData);
   
  }, [latestResults, category, ratingFilter, maxDistance, location, sortBy, userCoordinates, categoryOptions, isPhysicalLocation]);

  


  //console.log('category', category)
  //console.log('categoryOptions', categoryOptions)
  const handleFindBusiness = () => navigate('/find');
  const handleAddBusiness = () => navigate('/company/new');


  const handleDiscoverFromNetwork = (isChecked) => {
    console.log('isChecked', isChecked)
    getCacheData(cacheId);
    setFetchResultsTrigger(!fetchResultsTrigger);
  };

  

  console.log('filteredResults', filteredResults)


  const paginatedResults = filteredResults.slice(
    (page - 1) * resultsPerPage,
    page * resultsPerPage
  );

  console.log('paginatedResults', paginatedResults)

  const isValidCoordinates = (coordinates) =>
    Array.isArray(coordinates) &&
    coordinates.length === 2 &&
    coordinates[0] != null &&
    coordinates[1] != null;

  const hasActiveFilters = () => {
    return category !== 'All Categories' ||
            ratingFilter > 0 ||
            maxDistance !== 'all' ||
            showInsidersOnly;
  };
  
  const handleResetFilters = () => {
    setCategory('All Categories');
    setRatingFilter(0);
    setMaxDistance('all');
    setShowInsidersOnly(false);
    setPage(1);
  };

  // 🆕 Generate SEO data
  const seoData = generateSEOData();
  const searchResultsSchema = generateSearchResultsSchema(query, searchLocation, filteredResults, filteredResults.length);
  const businessSchemas = generateLocalBusinessSchema(filteredResults);

  if (loading) {
    return (
      <>
        {/* 🆕 SEO for loading state */}
        <EnhancedSEO 
          title={`Searching for ${query || 'businesses'}... - Revfer`}
          description="Loading search results for local businesses. Find trusted businesses with authentic reviews and AI-powered recommendations."
          keywords="business search, loading results, local businesses, business directory"
          type="website"
          image="/og-image.jpg"
        />
        
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <InsightsSkeleton />
            </Grid>
            <Grid item xs={12} md={6}>
              {Array.from({ length: 5 }).map((_, index) => (
                <ResultCardSkeleton key={index} />
              ))}
            </Grid>
            <Grid item xs={12} md={3}>
              <InsightsSkeleton />
            </Grid>
          </Grid>
        </Container>
      </>
    );
  }

  return (
    <>
      {/* 🆕 Comprehensive SEO Implementation */}
      <EnhancedSEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        type="website"
        image="/og-image.jpg"
        author="Revfer Search Team"
        dateModified={new Date().toISOString()}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Find Businesses', url: '/find' },
          { name: 'Search Results', url: '/searchresults' }
        ]}
      />

      <Helmet>
        {/* 🆕 Search Results Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(searchResultsSchema)}
        </script>
        
        {/* 🆕 Individual Business Schemas */}
        {businessSchemas.map((schema, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}

        {/* 🆕 Search-specific meta tags */}
        <meta name="search-query" content={query || ''} />
        <meta name="search-location" content={searchLocation || ''} />
        <meta name="results-count" content={seoData.totalResults.toString()} />
        <meta name="page-type" content="SearchResults" />
        
        {/* 🆕 Filter state meta tags */}
        <meta name="current-category" content={category} />
        <meta name="current-sort" content={sortBy} />
        <meta name="current-page" content={page.toString()} />
        
        {/* 🆕 Local SEO tags */}
        {searchLocation && (
          <>
            <meta name="geo.region" content="US" />
            <meta name="geo.placename" content={searchLocation} />
          </>
        )}
        
        {/* 🆕 Results-specific Open Graph */}
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:type" content="website" />
        
        {/* 🆕 Pagination handling */}
        {page > 1 && <meta name="robots" content="noindex, follow" />}
        
        {/* 🆕 Filter combinations that shouldn't be indexed */}
        {(hasActiveFilters() && page > 1) && <meta name="robots" content="noindex, follow" />}
      </Helmet>

      <main itemScope itemType="https://schema.org/SearchResultsPage">
        {/* 🆕 Hidden H1 for SEO */}
        <Typography 
          component="h1" 
          sx={{ 
            position: 'absolute', 
            left: '-10000px', 
            top: 'auto', 
            width: '1px', 
            height: '1px', 
            overflow: 'hidden' 
          }}
          itemProp="name"
        >
          {seoData.title}
        </Typography>

        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Updated Header with SEO */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3, border: 'none' }} component="header">
            <Typography 
              variant="h5" 
              align="center"
              component="h2" // 🆕 Proper heading hierarchy
              itemProp="headline"
            >
              {seoData.hasResults 
                ? `Found ${seoData.totalResults} ${query || 'businesses'}${searchLocation ? ` in ${searchLocation}` : ''}`
                : 'Explore Businesses in Your Area'
              }
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              align="center"
              itemProp="description"
            >
              {seoData.hasResults 
                ? 'Discover trusted businesses with authentic reviews and AI-powered insights.'
                : 'Discover new businesses and find trusted connections.'
              }
            </Typography>
          </Paper>


          <Grid container spacing={3}>
            {/* Left Column - Desktop Filters (Hidden on Mobile) */}
            {!isMobile && (
              <Grid item xs={12} md={3}>
                <CollapsibleFilters
                  category={category}
                  setCategory={setCategory}
                  categoryOptions={categoryOptions}
                  maxDistance={maxDistance}
                  setMaxDistance={setMaxDistance}
                  ratingFilter={ratingFilter}
                  setRatingFilter={setRatingFilter}
                  showInsidersOnly={showInsidersOnly}
                  setShowInsidersOnly={setShowInsidersOnly}
                  handleDiscoverFromNetwork={handleDiscoverFromNetwork} // EXISTING CACHE LOGIC
                  isAuthenticated={isAuthenticated}
                  userCoordinates={userCoordinates}
                  DISTANCE_OPTIONS={DISTANCE_OPTIONS}
                />
              </Grid>
            )}

            {/* Middle Column - Results */}
            <Grid item xs={12} md={isMobile ? 12 : 6}>
              {/* Mobile Action Bar */}
              {isMobile && (
                <MobileActionBar
                  onFilterClick={() => setMobileFiltersOpen(true)}
                  sortBy={sortBy}
                  setSortBy={setSortBy}
                  showMap={showMap}
                  setShowMap={setShowMap}
                  hasActiveFilters={hasActiveFilters()}
                  SORT_OPTIONS={SORT_OPTIONS}
                  handleFindBusiness={handleFindBusiness}
                  handleAddBusiness={handleAddBusiness}
                />
              )}

              {/* Desktop Action Bar - KEEP YOUR EXISTING DESKTOP ACTION BAR */}
              {!isMobile && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 3,
                    border: 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                  }}
                  component="nav"
                  aria-label="Search actions and sorting"
                >
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SearchIcon />}
                      onClick={handleFindBusiness}
                      aria-label="Start new business search"
                    >
                      Find Businesses
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddBusinessIcon />}
                      onClick={handleAddBusiness}
                      aria-label="Add your business to directory"
                    >
                      Add a Business
                    </Button>
                  </Box>

                  <FormControl sx={{ minWidth: 120 }}>
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      displayEmpty
                      inputProps={{ 'aria-label': 'Sort search results by' }}
                    >
                      {SORT_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              )}

              {/* Quick Filter Chips (Mobile only) */}
              {isMobile && (
                <QuickFilterChips
                  category={category}
                  setCategory={setCategory}
                  categoryOptions={categoryOptions}
                  ratingFilter={ratingFilter}
                  setRatingFilter={setRatingFilter}
                  maxDistance={maxDistance}
                  setMaxDistance={setMaxDistance}
                />
              )}

              {/* Map - KEEP YOUR EXISTING MAP CODE */}
              <Box sx={{ mb: 2 }}>
                {!isMobile && (
                  <Button
                    variant="outlined"
                    startIcon={<MapIcon />}
                    onClick={() => setShowMap(!showMap)}
                    sx={{ mb: 1 }}
                    aria-label={showMap ? 'Hide map view' : 'Show map view'}
                  >
                    {showMap ? 'Hide Map' : 'Show Map'}
                  </Button>
                )}
                <Collapse in={showMap}>
                  <BusinessMap 
                    businesses={filteredResults}
                    userLocation={userCoordinates.lat && userCoordinates.lng ? {
                      lat: userCoordinates.lat,
                      lng: userCoordinates.lng
                    } : null}
                    searchCenter={searchCenterCoordinates ? {
                      lat: searchCenterCoordinates[1],
                      lng: searchCenterCoordinates[0]
                    } : null}
                  />
                </Collapse>
              </Box>

              {/* Results - KEEP YOUR EXISTING RESULTS CODE */}
              {filteredResults.length > 0 ? (
                <section 
                  aria-label="Search results"
                  itemScope 
                  itemType="https://schema.org/ItemList"
                >
                  <meta itemProp="numberOfItems" content={filteredResults.length} />
                  
                  {paginatedResults.map((result, index) => (
                    <Paper 
                      variant="outlined" 
                      key={result.id} 
                      sx={{ p: 2, mb: 2, border: 'none', cursor: 'pointer' }}
                      onClick={() => navigate(`/business/${result._id}/${result.businessName.replace(/[\s,\.]+/g, '-').toLowerCase()}`)}
                      component="article"
                      itemScope
                      itemType="https://schema.org/LocalBusiness"
                      itemProp="itemListElement"
                      role="button"
                      tabIndex={0}
                      aria-label={`View ${result.businessName} business details`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/business/${result._id}/${result.businessName.replace(/[\s,\.]+/g, '-').toLowerCase()}`);
                        }
                      }}
                    >
                      <meta itemProp="position" content={(page - 1) * resultsPerPage + index + 1} />
                      
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Avatar
                          variant="rounded"
                          src={result.photoUrl}
                          sx={{ width: 64, height: 64 }}
                          itemProp="image"
                        >
                          {result.businessName[0]}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography 
                            variant="h6"
                            component="h3" // 🆕 Proper heading hierarchy
                            itemProp="name"
                          >
                            {result.businessName}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="textSecondary"
                            itemProp="category"
                          >
                            {result.skillName.charAt(0).toUpperCase() + result.skillName.slice(1)}
                          </Typography>
                          {result.businessType === CATEGORY_TYPES.PHYSICAL && (
                            <Box 
                              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                              itemScope
                              itemType="https://schema.org/PostalAddress"
                            >
                              <LocationIcon fontSize="small" color="action" />
                              <Typography 
                                variant="body2"
                                itemProp="streetAddress"
                              >
                                {result.geocodeResults.formattedAddress || 'Global'} 
                              </Typography>
                            </Box>
                          )}
                          {result.businessType === CATEGORY_TYPES.ONLINE && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LanguageIcon fontSize="small" color="action" />
                              <Typography variant="body2">
                                {result.geocodeResults.formattedAddress || 'Global'} 
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Box 
                          sx={{ textAlign: 'right' }}
                          itemScope
                          itemType="https://schema.org/AggregateRating"
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <Rating 
                              value={result.avgSkillRating_all} 
                              precision={0.1} 
                              readOnly 
                              size="small"
                              aria-label={`Rating: ${result.avgSkillRating_all} out of 5 stars`}
                            />
                            <Typography variant="body2" sx={{ ml: 1 }}>
                              ({result.count_all})
                            </Typography>
                          </Box>
                          <meta itemProp="ratingValue" content={result.avgSkillRating_all} />
                          <meta itemProp="reviewCount" content={result.count_all} />
                          <meta itemProp="bestRating" content="5" />
                          <meta itemProp="worstRating" content="1" />
                          
                          <Typography variant="body2" color="textSecondary">
                            Smart Rating: {Math.round(result.SkillValue_all*10)/10} 
                          </Typography>
                        </Box>
                      </Box>

                      <Typography 
                        variant="body2" 
                        sx={{ mt: 2 }}
                        itemProp="description"
                      >
                        {result.description}
                      </Typography>

                      <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                        {result.featureMap?.map((feature) => (
                          <Chip
                            key={feature}
                            label={feature}
                            size="small"
                          />
                        ))}
                        {result.verified && (
                          <Chip
                            label="Verified"
                            color="success"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      
                      <Box sx={{ mt: 1 }}>
                        {isValidCoordinates(result.geocodeResults?.location?.coordinates) ? (
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ display: 'flex', alignItems: 'center' }}
                          >
                            <LocationIcon fontSize="small" sx={{ mr: 1 }} />
                            {calculateDistance(
                              userCoordinates.lat,
                              userCoordinates.lng,
                              result.geocodeResults.location.coordinates[1],
                              result.geocodeResults.location.coordinates[0]
                            ).toFixed(1)} miles away
                          </Typography>
                        ) : (
                          maxDistance !== 'all' && (
                            <Typography
                              variant="body2"
                              color="warning.main"
                              sx={{ display: 'flex', alignItems: 'center' }}
                            >
                              <WarningIcon fontSize="small" sx={{ mr: 1 }} />
                              No location information available
                            </Typography>
                          )
                        )}
                      </Box>

                      {/* 🆕 Hidden geo coordinates for SEO */}
                      {result.geocodeResults?.location?.coordinates && (
                        <div 
                          itemScope 
                          itemType="https://schema.org/GeoCoordinates" 
                          itemProp="geo"
                          style={{ display: 'none' }}
                        >
                          <meta itemProp="latitude" content={result.geocodeResults.location.coordinates[1]} />
                          <meta itemProp="longitude" content={result.geocodeResults.location.coordinates[0]} />
                        </div>
                      )}
                    </Paper>
                  ))}

                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={Math.ceil(filteredResults.length / resultsPerPage)}
                      page={page}
                      onChange={(e, value) => setPage(value)}
                      color="primary"
                      aria-label="Search results pagination"
                    />
                  </Box>
                </section>
              ) : (
                <NoResultsFound
                  hasActiveFilters={hasActiveFilters()}
                  onResetFilters={handleResetFilters}
                  onBackToSearch={() => navigate('/find')}
                  searchQuery={query}
                />
              )}
            </Grid>

            {/* Right Column - Desktop Insights (Hidden on Mobile) */}
            {!isMobile && (
              <Grid item xs={12} md={3}>
                <CollapsibleInsights 
                  insights={randomInsights} 
                  isLoading={loading} 
                />
              </Grid>
            )}
          </Grid>

          {/* Mobile Insights (at bottom) */}
          {isMobile && (
            <Box sx={{ mt: 4 }}>
              <CollapsibleInsights 
                insights={randomInsights} 
                isLoading={loading} 
              />
            </Box>
          )}

          {/* Mobile Filter Drawer */}
          <MobileFilterDrawer
            open={mobileFiltersOpen}
            onClose={() => setMobileFiltersOpen(false)}
            category={category}
            setCategory={setCategory}
            categoryOptions={categoryOptions}
            maxDistance={maxDistance}
            setMaxDistance={setMaxDistance}
            ratingFilter={ratingFilter}
            setRatingFilter={setRatingFilter}
            showInsidersOnly={showInsidersOnly}
            setShowInsidersOnly={setShowInsidersOnly}
            handleDiscoverFromNetwork={handleDiscoverFromNetwork} // EXISTING CACHE LOGIC
            isAuthenticated={isAuthenticated}
            userCoordinates={userCoordinates}
            hasActiveFilters={hasActiveFilters}
            onResetFilters={handleResetFilters}
            DISTANCE_OPTIONS={DISTANCE_OPTIONS}
          />
        </Container>
      </main>
    </>
  );
}